import { ref, get, query, orderByChild, equalTo } from 'firebase/database';
import { database } from '../firebase';
import { atsAnalyticsApi } from './atsAnalyticsService';

const DAY_MS = 24 * 60 * 60 * 1000;

export const MAX_VISIBLE_JOBS = 2;
export const MAX_COMPARE_CANDIDATES = 2;

export const CHART_COLORS = [
  '#568ded',
  '#40c7eb',
  '#40eb6e',
  '#eb8d40',
  '#cc40eb',
  '#eb4040'
];

export const percent = (value, total) => {
  if (!total) return 0;
  return Math.round((value / total) * 100);
};

export const average = (values) => {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

export const toTimestamp = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const getObjectValues = (value) => {
  if (!value || typeof value !== 'object') return [];
  return Object.values(value);
};

const dedupe = (items) => [...new Set(items.filter(Boolean))];

const extractRatings = (scores) => getObjectValues(scores)
  .map((entry) => Number(typeof entry === 'number' ? entry : entry?.rating))
  .filter((value) => Number.isFinite(value));

const currentCandidateFitScore = (record) => {
  const rawValue = record?.candidateEvaluation?.current?.fitScore ?? record?.fit_score;
  if (rawValue === null || rawValue === undefined || rawValue === '') return null;
  const value = Number(rawValue);
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
};

const summarizeMessages = (messages) => {
  const items = getObjectValues(messages);
  const latestTimestamp = items.reduce((latest, item) => Math.max(latest, toTimestamp(item?.timestamp)), 0);
  return {
    count: items.length,
    latestTimestamp
  };
};

const summarizeNotes = (notes) => {
  const items = getObjectValues(notes);
  const latestTimestamp = items.reduce((latest, item) => Math.max(latest, toTimestamp(item?.timestamp)), 0);
  return {
    count: items.length,
    latestTimestamp
  };
};

const summarizeAssessments = (assessments) => {
  const items = getObjectValues(assessments);
  const performanceScores = [];
  let invitedCount = 0;
  let completedCount = 0;

  items.forEach((item) => {
    invitedCount += 1;
    const status = item?.response?.status;
    if (['completed', 'evaluated', 'submitted', 'manual_review'].includes(status)) {
      completedCount += 1;
    }
    const explicitValue = item?.evaluation?.result?.performance_score;
    const legacyValue = item?.evaluation?.result?.fit_score;
    // Number(null) is zero in JavaScript. Guard nullish values first so an
    // ungradable/manual-review report never becomes a fabricated 0/100.
    const explicitPerformance = explicitValue === null || explicitValue === undefined || explicitValue === ''
      ? null
      : Number(explicitValue);
    const legacyTenPointScore = legacyValue === null || legacyValue === undefined || legacyValue === ''
      ? null
      : Number(legacyValue);
    const performanceScore = Number.isFinite(explicitPerformance)
      ? explicitPerformance
      : (Number.isFinite(legacyTenPointScore) ? legacyTenPointScore * 10 : null);
    if (Number.isFinite(performanceScore)) {
      performanceScores.push(Math.max(0, Math.min(100, performanceScore)));
    }
  });

  return {
    invitedCount,
    completedCount,
    performanceScores,
    avgPerformanceScore: performanceScores.length ? average(performanceScores) : 0
  };
};

const summarizeInterviews = (interviews) => {
  const items = Array.isArray(interviews) ? interviews : getObjectValues(interviews);
  const now = Date.now();
  let completedCount = 0;
  let upcomingCount = 0;
  let latestTimestamp = 0;

  items.forEach((interview) => {
    const timestamp = toTimestamp(interview?.dateTime || interview?.createdAt || interview?.rescheduledAt);
    latestTimestamp = Math.max(latestTimestamp, timestamp);

    if (!timestamp) return;
    if (timestamp <= now) {
      completedCount += 1;
    } else {
      upcomingCount += 1;
    }
  });

  return {
    count: items.length,
    completedCount,
    upcomingCount,
    latestTimestamp
  };
};

export const TALENT_POOL_EXTRACTION_SCHEMA = {
  schemaVersion: 2,
  minConfidence: 0.7,
  required: [
    'identity.fullName',
    'experience.totalYears',
    'experience.primaryPosition',
    'experience.seniority',
    'skills.normalized'
  ]
};

const getByPath = (payload, path) => {
  if (!payload || typeof payload !== 'object') return undefined;
  return String(path).split('.').reduce((current, part) => {
    if (!current || typeof current !== 'object') return undefined;
    return current[part];
  }, payload);
};

const getCandidateStatus = (profile) => {
  if (profile.activeConnectionsCount > 0) return 'active';
  if (profile.insightEligible) return 'qualified';
  if (profile.missingSignals.length >= 3) return 'needs-enrichment';
  return 'archive-only';
};

const buildCandidateSummary = (profile) => {
  const position = profile.primaryPosition || 'Position missing';
  const seniority = profile.primarySeniority || 'Seniority missing';
  const years = Number(profile.totalYearsExperience);
  const yearsLabel = Number.isFinite(years) && years > 0 ? `${years}y` : 'Experience missing';
  return `${position} | ${seniority} | ${yearsLabel}`;
};

const buildImportedProfileInsights = (profile) => {
  const activeConnections = profile.activeConnections || [];
  const historyEntries = getObjectValues(profile.history).sort((a, b) => (toTimestamp(b?.archivedAt) - toTimestamp(a?.archivedAt)));
  const liveInterviews = Array.isArray(profile.liveInterviews) ? profile.liveInterviews : [];

  const currentRatings = activeConnections.flatMap((connection) => extractRatings(connection?.scores));
  const archivedRatings = historyEntries.flatMap((entry) => extractRatings(entry?.scores));
  const allRatings = [...currentRatings, ...archivedRatings];

  const currentMessages = activeConnections.map((connection) => summarizeMessages(connection?.messages));
  const archivedMessages = historyEntries.map((entry) => summarizeMessages(entry?.messages));

  const currentNotes = activeConnections.map((connection) => summarizeNotes(connection?.notes));
  const archivedNotes = historyEntries.map((entry) => summarizeNotes(entry?.notes));

  const currentAssessments = activeConnections.map((connection) => summarizeAssessments(connection?.assessments));
  const archivedAssessments = historyEntries.map((entry) => summarizeAssessments(entry?.assessments));

  const liveInterviewSummary = summarizeInterviews(liveInterviews);
  const archivedInterviewSummary = historyEntries.map((entry) => summarizeInterviews(entry?.interviews));

  const notesCount = [...currentNotes, ...archivedNotes].reduce((sum, item) => sum + item.count, 0);
  const messagesCount = [...currentMessages, ...archivedMessages].reduce((sum, item) => sum + item.count, 0);
  const interviewCount = liveInterviewSummary.count + archivedInterviewSummary.reduce((sum, item) => sum + item.count, 0);
  const upcomingInterviews = liveInterviewSummary.upcomingCount;
  const completedInterviews = liveInterviewSummary.completedCount + archivedInterviewSummary.reduce((sum, item) => sum + item.completedCount, 0);
  const assessmentsInvited = [...currentAssessments, ...archivedAssessments].reduce((sum, item) => sum + item.invitedCount, 0);
  const assessmentsCompleted = [...currentAssessments, ...archivedAssessments].reduce((sum, item) => sum + item.completedCount, 0);
  // Candidate fit is job-specific and comes from the cumulative ledger. An
  // assessment's isolated performance score is a different metric.
  const fitScores = [...activeConnections, ...historyEntries]
    .map(currentCandidateFitScore)
    .filter((value) => value !== null);
  const assessmentPerformanceScores = [...currentAssessments, ...archivedAssessments]
    .flatMap((item) => item.performanceScores);
  const avgFitScore = fitScores.length ? average(fitScores) : 0;
  const avgAssessmentPerformance = assessmentPerformanceScores.length
    ? average(assessmentPerformanceScores)
    : 0;
  const hiredCount = historyEntries.filter((entry) => entry?.isHired).length + activeConnections.filter((connection) => connection?.isHired).length;

  const latestSignalPoints = [
    toTimestamp(profile.createdAt),
    ...activeConnections.map((connection) => Math.max(
      toTimestamp(connection?.lastUpdated),
      toTimestamp(connection?.lastStageChange),
      toTimestamp(connection?.lastInterviewScheduled),
      toTimestamp(connection?.lastInterviewRescheduled)
    )),
    ...currentMessages.map((item) => item.latestTimestamp),
    ...currentNotes.map((item) => item.latestTimestamp),
    ...archivedMessages.map((item) => item.latestTimestamp),
    ...archivedNotes.map((item) => item.latestTimestamp),
    ...archivedInterviewSummary.map((item) => item.latestTimestamp),
    liveInterviewSummary.latestTimestamp,
    ...historyEntries.map((entry) => toTimestamp(entry?.archivedAt))
  ];

  const lastTouchedAt = latestSignalPoints.reduce((latest, value) => Math.max(latest, value || 0), 0);
  const daysSinceLastTouch = lastTouchedAt ? Math.max(0, Math.floor((Date.now() - lastTouchedAt) / DAY_MS)) : 999;

  const hasCV = Boolean(profile.cvLink);
  const hasEmail = Boolean(profile.email);
  const hasPhone = Boolean(profile.phone || profile.contact?.phone || profile.contactInfo?.phone);
  const hasAnalysis = Boolean(profile.applicantAnalysis || historyEntries.some((entry) => Boolean(entry?.applicantAnalysis)));
  const currentJobs = dedupe([
    ...(profile.assigned_jobs || []),
    ...activeConnections.map((connection) => connection?.jobAppliedFor)
  ]);
  const isStarred = activeConnections.some((connection) => connection?.starred === true);
  const unreadEmployerMessages = activeConnections.filter((connection) => connection?.newMsgEmpl === 'true').length;

  const extraction = profile.extraction || {};
  const persistedAnalytics = profile.analytics && typeof profile.analytics === 'object' ? profile.analytics : {};
  const processing = profile.processing && typeof profile.processing === 'object' ? profile.processing : {};
  const requiredFields = TALENT_POOL_EXTRACTION_SCHEMA.required || [];
  const minConfidence = Number(TALENT_POOL_EXTRACTION_SCHEMA.minConfidence || 0.7);
  const extractionIdentity = extraction?.identity || {};
  const extractionExperience = extraction?.experience || {};
  const extractionSkills = extraction?.skills || {};
  const extractionFit = extraction?.fit || {};

  const primaryPosition = extractionExperience?.primaryPosition || extractionExperience?.title || '';
  const primarySeniority = extractionExperience?.seniority || '';
  const totalYearsExperience = Number(extractionExperience?.totalYears || 0);
  const normalizedSkills = Array.isArray(extractionSkills?.normalized) ? extractionSkills.normalized : [];

  const extractionChecks = [
    Boolean(extractionIdentity?.fullName || profile.name),
    Boolean(extractionIdentity?.email || profile.email),
    Boolean(extractionIdentity?.phone || profile.phone),
    Boolean(primaryPosition),
    Boolean(primarySeniority),
    Number.isFinite(totalYearsExperience) && totalYearsExperience > 0,
    normalizedSkills.length > 0,
    Number.isFinite(Number(extractionFit?.roleCoverage))
  ];
  const extractionCoverage = percent(extractionChecks.filter(Boolean).length, extractionChecks.length);
  const extractionStatus = extractionCoverage === 0 ? 'pending' : extractionCoverage >= 80 ? 'validated' : 'partial';
  const missingRequiredFields = [];
  const lowConfidenceFields = [];
  requiredFields.forEach((fieldPath) => {
    const fieldValue = getByPath(extraction, fieldPath);
    const hasValue = !(fieldValue === undefined || fieldValue === null || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0));
    if (!hasValue) {
      missingRequiredFields.push(fieldPath);
      return;
    }
    const confidenceScore = Number(getByPath(extraction, `confidence.${fieldPath}`) ?? 0);
    if (!Number.isFinite(confidenceScore) || confidenceScore < minConfidence) {
      lowConfidenceFields.push(fieldPath);
    }
  });
  const computedInsightEligible = missingRequiredFields.length === 0 && lowConfidenceFields.length === 0;
  const insightEligible = typeof persistedAnalytics?.insightEligible === 'boolean'
    ? persistedAnalytics.insightEligible
    : computedInsightEligible;

  const missingSignals = [];
  if (!hasCV) missingSignals.push('CV');
  if (!hasEmail && !hasPhone) missingSignals.push('Direct contact');
  if (!primaryPosition) missingSignals.push('Primary position');
  if (!primarySeniority) missingSignals.push('Seniority');
  if (!(Number.isFinite(totalYearsExperience) && totalYearsExperience > 0)) missingSignals.push('Experience years');
  if (!normalizedSkills.length) missingSignals.push('Normalized skills');

  const status = getCandidateStatus({
    activeConnectionsCount: activeConnections.length,
    insightEligible,
    extractionCoverage,
    missingSignals
  });

  return {
    ...profile,
    activeConnections,
    historyEntries,
    liveInterviews,
    currentJobs,
    activeConnectionsCount: activeConnections.length,
    notesCount,
    messagesCount,
    interviewCount,
    upcomingInterviews,
    completedInterviews,
    assessmentsInvited,
    assessmentsCompleted,
    avgRating: allRatings.length ? average(allRatings) : 0,
    ratingCount: allRatings.length,
    avgFitScore,
    avgAssessmentPerformance,
    hiredCount,
    lastTouchedAt,
    daysSinceLastTouch,
    hasCV,
    hasEmail,
    hasPhone,
    hasAnalysis,
    isStarred,
    unreadEmployerMessages,
    extractionCoverage: Number(persistedAnalytics?.extractionCoverage ?? extractionCoverage),
    extractionStatus,
    insightEligible,
    missingRequiredFields: Array.isArray(persistedAnalytics?.missingRequiredFields) ? persistedAnalytics.missingRequiredFields : missingRequiredFields,
    lowConfidenceFields: Array.isArray(persistedAnalytics?.lowConfidenceFields) ? persistedAnalytics.lowConfidenceFields : lowConfidenceFields,
    primaryPosition,
    primarySeniority,
    totalYearsExperience,
    normalizedSkills,
    missingSignals,
    status,
    analytics: persistedAnalytics,
    processing,
    parsingStatus: processing?.status
      || (profile.extraction || profile.analytics || primaryPosition ? 'parsed' : (hasCV ? 'idle' : 'blocked')),
    parsingModel: processing?.model || '',
    estimatedCost: Number(processing?.estimatedCost || 0),
    tokenUsage: processing?.tokenUsage || null,
    lastParsedAt: toTimestamp(processing?.completedAt),
    summary: buildCandidateSummary({
      ...profile,
      activeConnectionsCount: activeConnections.length,
      primaryPosition,
      primarySeniority,
      totalYearsExperience
    })
  };
};

const safeGet = async (label, readFn, fallbackValue = null) => {
  try {
    return await readFn();
  } catch (error) {
    console.warn(`Imported talent pool read failed for ${label}:`, error);
    return fallbackValue;
  }
};

const buildImportedConnectionKeysFromIndex = (indexRoot) => {
  const keys = new Set();
  if (!indexRoot || typeof indexRoot !== 'object') return keys;

  Object.values(indexRoot).forEach((jobBucket) => {
    if (!jobBucket || typeof jobBucket !== 'object') return;
    Object.entries(jobBucket).forEach(([connectionKey, indexEntry]) => {
      const applicantId = indexEntry?.applicantId || '';
      const isImported = indexEntry?.applicantType === 'imported'
        || connectionKey.includes('_imported_')
        || String(applicantId).startsWith('temp_');

      if (isImported) {
        keys.add(connectionKey);
      }
    });
  });

  return keys;
};

export const loadImportedTalentPoolProfiles = async (companyUid) => {
  const byApplicant = {};

  const ensureProfile = (applicantId) => {
    if (!byApplicant[applicantId]) {
      byApplicant[applicantId] = {
        applicantId,
        name: 'Unknown',
        email: '',
        phone: '',
        createdAt: 0,
        lastUpdated: 0,
        passedFilter: false,
        connectionKeys: new Set(),
        assigned_jobs: [],
        source: 'imported',
        cvLink: '',
        fileName: 'resume.pdf',
        isArchived: true,
        history: {},
        parsedData: null,
        extraction: null,
        analytics: null,
        processing: null,
        applicantAnalysis: null,
        contact: null,
        contactInfo: null,
        activeConnections: [],
        liveInterviews: []
      };
    }
    return byApplicant[applicantId];
  };

  const mergeProfileNode = (applicantId, profileData, sourceFallback) => {
    if (!profileData || typeof profileData !== 'object') return;
    const profile = ensureProfile(applicantId);
    profile.name = profileData?.name || profile.name;
    profile.email = profileData?.email || profile.email;
    profile.phone = profileData?.phone || profile.phone || profileData?.contact?.phone || profileData?.contactInfo?.phone || '';
    profile.createdAt = toTimestamp(profileData?.createdAt) || profile.createdAt;
    profile.lastUpdated = toTimestamp(profileData?.lastUpdated) || profile.lastUpdated;
    profile.passedFilter = Boolean(profile.passedFilter || profileData?.passedFilter);
    profile.assigned_jobs = dedupe([...(profile.assigned_jobs || []), ...(profileData?.assigned_jobs || [])]);
    profile.source = profileData?.source || profileData?.uploadSource || profile.source || sourceFallback;
    profile.cvLink = profileData?.cv?.Link || profileData?.cv?.link || profileData?.cv?.url || (typeof profileData?.cv === 'string' ? profileData.cv : profile.cvLink);
    profile.fileName = profileData?.cv?.fileName || profileData?.cv?.filename || profile.fileName;
    profile.history = { ...profile.history, ...(profileData?.history || {}) };
    profile.parsedData = profile.parsedData || profileData?.parsedData || null;
    profile.extraction = profile.extraction || profileData?.extraction || null;
    profile.analytics = profile.analytics || profileData?.analytics || null;
    profile.processing = profile.processing || profileData?.processing || null;
    profile.applicantAnalysis = profile.applicantAnalysis || profileData?.applicantAnalysis || null;
    profile.contact = profile.contact || profileData?.contact || null;
    profile.contactInfo = profile.contactInfo || profileData?.contactInfo || null;
  };

  const [newProfilesSnap, legacyProfilesSnap, applicantIndexSnap, interviewsSnap] = await Promise.all([
    safeGet('imported_profiles/company', () => get(ref(database, `imported_profiles/${companyUid}`))),
    safeGet('imported_profiles/legacy', () => get(query(ref(database, 'imported_profiles'), orderByChild('uploadedBy'), equalTo(companyUid)))),
    safeGet('companies/applicant_index', () => get(ref(database, `companies/${companyUid}/applicant_index/by_job`))),
    safeGet('companies/interviews', () => get(ref(database, `companies/${companyUid}/interviews`)))
  ]);

  if (newProfilesSnap?.exists?.()) {
    Object.entries(newProfilesSnap.val()).forEach(([applicantId, profileData]) => {
      mergeProfileNode(applicantId, profileData, 'imported');
    });
  }

  if (legacyProfilesSnap?.exists?.()) {
    Object.entries(legacyProfilesSnap.val()).forEach(([applicantId, profileData]) => {
      mergeProfileNode(applicantId, profileData, 'imported_legacy');
    });
  }

  const importedConnectionKeys = buildImportedConnectionKeysFromIndex(applicantIndexSnap?.val?.());
  const connectionSnapshots = await Promise.allSettled(
    [...importedConnectionKeys].map((connectionKey) => get(ref(database, `connections/${connectionKey}`)))
  );

  connectionSnapshots.forEach((result, index) => {
    const connectionKey = [...importedConnectionKeys][index];
    if (result.status !== 'fulfilled' || !result.value?.exists?.()) return;

    const connectionData = result.value.val();
    const applicantId = connectionData?.tempProfileId || connectionKey.split('_imported_')[1]?.split('_copy_')[0];
    if (!applicantId) return;

    const profile = ensureProfile(applicantId);
    profile.connectionKeys.add(connectionKey);
    profile.assigned_jobs = dedupe([...(profile.assigned_jobs || []), connectionData?.jobAppliedFor]);
    profile.passedFilter = Boolean(profile.passedFilter || connectionData?.passedFilter);
    profile.lastUpdated = Math.max(profile.lastUpdated, toTimestamp(connectionData?.lastUpdated), toTimestamp(connectionData?.lastStageChange));
    profile.applicantAnalysis = profile.applicantAnalysis || connectionData?.applicantAnalysis || null;
    profile.contactInfo = profile.contactInfo || connectionData?.contactInfo || null;
    profile.activeConnections.push({
      ...connectionData,
      connectionKey
    });
  });

  if (interviewsSnap?.exists?.()) {
    Object.entries(interviewsSnap.val()).forEach(([applicantId, interviews]) => {
      if (!byApplicant[applicantId]) return;
      byApplicant[applicantId].liveInterviews = getObjectValues(interviews);
    });
  }

  return Object.values(byApplicant)
    .map((profile) => ({
      ...buildImportedProfileInsights(profile),
      connectionKeys: Array.from(profile.connectionKeys || [])
    }))
    .sort((a, b) => (b.lastTouchedAt || b.createdAt || 0) - (a.lastTouchedAt || a.createdAt || 0));
};

export const loadTalentPoolSummary = async (companyUid) => {
  if (!companyUid) return null;

  try {
    const response = await fetch(`${atsAnalyticsApi.backendUrl}/analytics/v2/talent-pool?company_uid=${encodeURIComponent(companyUid)}`);
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body?.detail || body?.error || `HTTP ${response.status}`);
    }
    return body;
  } catch (error) {
    console.warn('Failed to load backend talent-pool summary:', error);
    return null;
  }
};

export const extractTalentPoolFromProfile = async ({
  companyUid,
  applicantId,
  source = 'profile_derivation',
  priority = null,
  force = false,
  requestedByUser = null
}) => {
  if (!companyUid || !applicantId) {
    throw new Error('companyUid and applicantId are required');
  }
  const response = await fetch(`${atsAnalyticsApi.backendUrl}/analytics/v2/talent-pool/extract`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      company_uid: companyUid,
      applicant_id: applicantId,
      source,
      priority,
      force,
      requested_by_user: requestedByUser
    })
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body?.detail || body?.error || `HTTP ${response.status}`);
  }
  return body;
};

export const queueTalentPoolProfilesForParsing = async ({
  companyUid,
  applicantIds,
  source = 'bulk_upload',
  priority = null,
  force = false,
  requestedByUser = null
}) => {
  if (!companyUid || !Array.isArray(applicantIds) || !applicantIds.length) {
    throw new Error('companyUid and applicantIds are required');
  }
  const response = await fetch(`${atsAnalyticsApi.backendUrl}/analytics/v2/talent-pool/extract/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      company_uid: companyUid,
      applicant_ids: applicantIds,
      source,
      priority,
      force,
      requested_by_user: requestedByUser
    })
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body?.detail || body?.error || `HTTP ${response.status}`);
  }
  return body;
};

export const syncTalentPoolParsingBatches = async ({ companyUid, batchId = null }) => {
  if (!companyUid) {
    throw new Error('companyUid is required');
  }
  const response = await fetch(`${atsAnalyticsApi.backendUrl}/analytics/v2/talent-pool/parsing/sync-batches`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      company_uid: companyUid,
      batch_id: batchId
    })
  });
  const body = await response.json();
  if (!response.ok) {
    throw new Error(body?.detail || body?.error || `HTTP ${response.status}`);
  }
  return body;
};
